import React, { useState } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from 'react-router-dom';
import { TbHome } from "react-icons/tb";
import { HiOutlineSpeakerphone } from "react-icons/hi";
import { FaPhotoVideo, FaWallet } from 'react-icons/fa';
import { MdOutlinePendingActions } from 'react-icons/md';
import { AiOutlineLogout } from "react-icons/ai";
import { logout } from "../../../store/publicStore/actions/utilsActions/utilsActions";
import { SidebarImg } from '../../../static/constant/imgConstants';

const BrandSidebar = ({ isOpen, onClose }) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const location = useLocation();
    const getUserInfoReducer = useSelector(state => state.getUserInfoReducer);

    // Sidebar data state
    const [sidebarData, setSidebarData] = useState([
        { id: "1", title: "Home", icon: <TbHome size={20} className="mx-[1.8rem]" />, path: ["/brand"], isActive: true, subMenuItems: [] },
        { id: "2", title: "Transaction", icon: <FaWallet size={20} className="mx-[1.8rem]" />, path: ["/brand/transactions"], isActive: false, subMenuItems: [] },
        { id: "3", title: "Campaigns", icon: <HiOutlineSpeakerphone size={20} className="mx-[1.8rem]" />, path: ["/brand/campaigns", "/brand/campaigns/create", "/brand/campaigns/details"], isActive: false, subMenuItems: [], },
        { id: "4", title: "UGC Content List", icon: <FaPhotoVideo size={22} className="mx-[1.8rem]" />, path: ["/brand/campaigns/campaign-list"], isActive: false, subMenuItems: [] },
        { id: "5", title: "Knowledge Base", externalLink:true, icon: <MdOutlinePendingActions size={22} className="mx-[1.8rem]" />, path: ["https://www.paidpromo.ai/knowledge"], isActive: false, subMenuItems: [] },
    ]);

    // Handle click to toggle `isActive`
    const handleItemClick = (id) => {
        setSidebarData((prevState) =>
            prevState.map((item) =>
                item.id === id
                    ? { ...item, isActive: !item.isActive }
                    : { ...item, isActive: false }
            )
        );
    };

    // Guard helpers for leaving create campaign with filled data
    const hasAnyValue = (obj) => {
        if (!obj || typeof obj !== 'object') return false;
        return Object.values(obj).some((v) => {
            if (v === null || v === undefined) return false;
            if (typeof v === 'string') return v.trim() !== '';
            if (Array.isArray(v)) return v.length > 0;
            if (typeof v === 'object') return hasAnyValue(v);
            return Boolean(v);
        });
    };

    const { brandCampaignCreationData } = useSelector(state => state.utilReducers);
    const isOnCreateCampaignPage = location.pathname === '/brand/campaigns/create';
    const shouldConfirmLeaveCreate = (targetPath) => {
        if (!isOnCreateCampaignPage) return false;
        if (targetPath === '/brand/campaigns/create') return false;
        return hasAnyValue(brandCampaignCreationData);
    };

    const renderSidebarContent = () => (
        <>
            <div className="flex flex-col justify-between flex-grow w-full">
                {/* Mobile User Info and Wallet Section - Only visible on mobile */}
                <div className="lg:hidden flex flex-col items-center mb-8 border-b pb-6">
                    <div className="w-[6rem] h-[6rem] rounded-full mb-4">
                        <img 
                            className="w-full h-full rounded-full object-cover" 
                            src={SidebarImg.profile} 
                            alt="User Avatar" 
                        />
                    </div>
                    <span className="text-[1.6rem] font-semibold mb-4">
                        {getUserInfoReducer?.data?.firstName}
                    </span>
                    <button
                        className='w-[40%] md:w-[40%] h-[4rem] flex justify-center items-center bg-[#0D9B50] hover:bg-[#6ec396] rounded-full'
                        onClick={() => { 
                            getUserInfoReducer.data.userType === "BRAND" 
                                ? navigate("/brand/transactions") 
                                : navigate("/influencer/transactions");
                            onClose();
                        }}
                    >
                        <FaWallet size={20} color='white' className="mx-2" />
                        <span className="text-[#ffffff] text-[1.4rem]">
                            ${getUserInfoReducer?.data?.walletBalance}
                        </span>
                    </button>
                </div>

                {/* Sidebar Navigation Items */}
                <div className="flex flex-col justify-start items-start w-full h-max">
                    {sidebarData?.map((item) => (
                        <div className="flex flex-col justify-start items-start w-full h-max" key={item.id}>
                            <div
                                className={`bs-navs flex justify-start items-center w-full h-[5rem] text-[1.8rem] my-[.2rem] cursor-pointer ${
                                    item.path.includes(location.pathname) 
                                        ? "text-[#FFF] bg-[#0D9B50]" 
                                        : "text-[#202224]"
                                } rounded-[.8rem] hover:bg-[#0D9B50] hover:text-[#FFF]`}
                                onClick={() => {
                                    handleItemClick(item.id);
                                    const target = item.path[0];
                                    if (shouldConfirmLeaveCreate(target)) {
                                        const ok = window.confirm('You have unsaved campaign data. If you leave this page, you will lose the filled data. Do you want to continue?');
                                        if (!ok) {
                                            return;
                                        }
                                    }
                                    if (item.externalLink) {
                                        window.open(target, '_blank');
                                    } else {
                                        navigate(target);
                                    }
                                    onClose();
                                }}
                            >
                                {item.icon}
                                <h4>{item.title}</h4>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Logout Button - Now at the bottom */}
                <button
                    className="bs-navs flex justify-start items-center w-full h-[5rem] text-[1.8rem] my-[.2rem] cursor-pointer rounded-[.8rem] hover:bg-[#0D9B50] hover:text-[#FFF] mt-auto"
                    onClick={() => { 
                        dispatch(logout());
                        onClose();
                    }}
                >
                    <AiOutlineLogout size={20} className="mx-[1.8rem]" />
                    Logout
                </button>
            </div>
        </>
    );

    return (
        <>
            {/* Mobile Sidebar Overlay */}
            {isOpen && (
                <div 
                    className="lg:hidden fixed inset-0 bg-black/50 z-40"
                    onClick={onClose}
                >
                    <div 
                        className="fixed top-0 left-0 w-[80%] md:w-[40%] h-full bg-white shadow-lg overflow-y-auto"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="brand-sidebar w-full  h-full py-[4.5rem] flex flex-col justify-between items-start px-[2.4rem]">
                            {renderSidebarContent()}
                        </div>
                    </div>
                </div>
            )}

            {/* Desktop Sidebar */}
            <div className="hidden lg:flex brand-sidebar w-[36rem] py-[4.5rem] flex-col justify-between items-start pl-[2.4rem] pr-[5rem]">
                {renderSidebarContent()}
            </div>
        </>
    );
};

export default BrandSidebar;